import Modal from "/WebInterface/new-ui/modules/modal.js";
import Tabs from "/WebInterface/new-ui/modules/tabs.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";
import TwoFactorAuthSetup from "/WebInterface/new-ui/modules/two-factor-auth/two-factor-auth.js";
import UserOptionsTemplate from "./template.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import store from "/WebInterface/new-ui/store/index.js";
import { validate } from "/WebInterface/new-ui/assets/js/utils/validate.js";
import { Prompt } from "/WebInterface/new-ui/modules/alert.js";
import {
  changePassword,
  changePhone,
  confirmQR,
} from "/WebInterface/new-ui/assets/js/utils/api/apis.js";

export const initUserOptions = () => {
  const twoFactorAuthSetup = new TwoFactorAuthSetup();
  let changePasswordInFlight = false;
  let changePhoneInFlight = false;

  const userOptionsCustomizations = () => {
    const canHideUserOptionHideDotItems = utils.getUserCustomizationValue(
      "HideUserOptionHideDotItems"
    );
    const canHideUserOptionHideCheckBoxColumn = utils.getUserCustomizationValue(
      "HideUserOptionHideCheckBoxColumn"
    );
    const canHideUserOptionHideFilterSection = utils.getUserCustomizationValue(
      "HideUserOptionHideFilterSection"
    );
    const canHideUserOptionAutoStartUpload = utils.getUserCustomizationValue(
      "HideUserOptionAutoStartUpload"
    );
    const canHideUserOptionChangePhone = utils.getUserCustomizationValue(
      "HideUserOptionChangePhone"
    );
    const canHideUserOptionChangePassword = utils.getUserCustomizationValue(
      "HideUserOptionChangePassword"
    );
    const canHideUserOptionGeneratePassword = utils.getUserCustomizationValue(
      "HideUserOptionGeneratePassword"
    );
    const enableUserOption2fa =
      utils.getUserCustomizationValue("UserOption2fa");
    const showOnlyTwoFactorAuthTab = utils.getUserCustomizationValue(
      "useroptions_only_twofactor_google_enrollment"
    );

    return {
      canHideUserOptionHideDotItems,
      canHideUserOptionHideCheckBoxColumn,
      canHideUserOptionHideFilterSection,
      canHideUserOptionAutoStartUpload,
      canHideUserOptionChangePhone,
      canHideUserOptionChangePassword,
      canHideUserOptionGeneratePassword,
      enableUserOption2fa,
      showOnlyTwoFactorAuthTab,
    };
  };

  const customizations = userOptionsCustomizations();
  const userPrivOptions = store?.userInfo?.user_priv_options;

  const hasChangePasswordPermission = userPrivOptions.includes("(SITE_PASS)");

  // user option modal
  const dialog = new Modal({
    body: UserOptionsTemplate({
      ...customizations,
      hasChangePasswordPermission,
    }),
    minWidth: "780px",
    extraClasses: ["user-options-dialog"],
    title: "User Options",
    onBeforeHide: () => {
      twoFactorAuthSetup.hide();

      const tabs = _$(dialog.root).find(".tab-pane").sel();

      const tempUserOptions = {};

      tabs.forEach(({ id }) => {
        const inputs = _$(`#${id}`).find("input").sel();
        tempUserOptions[id] = {};

        inputs.forEach((input) => {
          let value = input.value;
          if (input.type === "checkbox") value = input.checked;
          tempUserOptions[id][input.id] = value;
        });
      });

      tempUserOptions.activeTabIndex = tab.getIndex();

      let isGeneratePasswordSectionOpen = true;
      if (_$("#userOptionsGeneratePassword").hasClass("is-hidden"))
        isGeneratePasswordSectionOpen = false;

      tempUserOptions.isGeneratePasswordSectionOpen =
        isGeneratePasswordSectionOpen;

      store.dispatch("setUserOptions", tempUserOptions);
    },
    onAfterShow: () => {
      twoFactorAuthSetup.renderTemplate({
        templateContainer: "#setup2FactorAuth",
        templateValues: {},
        onAfterTimeout: () => {
          dialog.update({
            buttons: [],
          });
        },
      });

      twoFactorAuthSetup.addSetupButtonListener(() => {
        const shouldShowQR = window.confirm(t("areYouReadyToScanQR"));
        if (!shouldShowQR) return;
        showQR();
      });
    },
  });

  const showQR = async () => {
    twoFactorAuthSetup.show();

    dialog.update({
      // Footer buttons for two factor auth tab
      buttons: [
        {
          label: "cancel",
          classes: ["secondary-button", "mr-3"],
          onClick: async () => {
            twoFactorAuthSetup.hide();
            dialog.update({
              buttons: [],
            });
          },
        },
        {
          label: "confirm",
          classes: ["primary-button", "mr-3"],
          onClick: async () => {
            try {
              const response = await confirmQR({
                qrid: twoFactorAuthSetup.UNIQUE_QR_ID,
              });
              growl("", response, 5000, false);

              twoFactorAuthSetup.hide();
              dialog.hide(true);
            } catch (error) {
              growl("", error, 5000, true);
            }
          },
        },
      ],
    });
  };

  // rendering value for user options(preference tab) from localStorage
  const userOptions = JSON.parse(localStorage.getItem("userOptions")) || {};
  const modalBody = dialog.modal.body;

  const options = Object.keys(userOptions);
  options.forEach((opt) => {
    const elem = _$(modalBody).find(`[name=${opt}]`, true);

    if (elem?.type === "checkbox") {
      elem.checked = userOptions[opt];
    }
  });

  // rendering value for user options from store
  const { userOptions: userOptionsStore } = store;
  const parentProperties = Object.keys(userOptionsStore);

  parentProperties.forEach((key) => {
    const option = userOptionsStore[key];
    const childrenProperties = Object.entries(option);

    if (childrenProperties.length > 0) {
      for (const [key, value] of childrenProperties) {
        const input = _$(`#${key}`).el();
        if (!input) return;
        if (input.type === "checkbox") {
          input.checked = value;
        } else {
          input.value = value || "";
        }
      }
    }
  });

  // forms in modal tab
  const changePasswordForm = _$("#userOptionsChangePasswordForm"); // tab 2 form
  const userOptionGeneratedPasswordWrapper = _$("#userOptionsGeneratePassword");
  const phoneNumberFormEl = _$("#userOptionsPhoneNumberForm").el();

  let savePhoneHandler = null;
  if (phoneNumberFormEl) {
    phoneNumberFormEl.addEventListener("submit", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (typeof savePhoneHandler === "function") {
        savePhoneHandler(e);
      }
    });
  }

  const applyPasswordRulesToChangePasswordForm = () => {
    if (!changePasswordForm.el()) return;

    const newPasswordInput = changePasswordForm.find(
      '[name="new_password1"]',
      true
    );

    if (!newPasswordInput) return;

    const toNumber = (value, fallback = 0) => {
      const parsedValue = parseInt(value, 10);
      return Number.isFinite(parsedValue) ? parsedValue : fallback;
    };

    const storeRules = store?.userInfo || {};
    const globalRules = window?.passwordRule || {};

    const hasGlobalMinLengthKey = Object.prototype.hasOwnProperty.call(
      globalRules,
      "min_password_length"
    );

    const minNumbers = toNumber(
      storeRules.min_password_numbers ?? globalRules.min_password_numbers
    );
    const minLowers = toNumber(
      storeRules.min_password_lowers ?? globalRules.min_password_lowers
    );
    const minUppers = toNumber(
      storeRules.min_password_uppers ?? globalRules.min_password_uppers
    );
    const minSpecials = toNumber(
      storeRules.min_password_specials ?? globalRules.min_password_specials
    );

    const unsafeChars =
      storeRules.unsafe_password_chars ??
      globalRules.unsafe_password_chars ??
      "";

    const resolveBaseMinLength = () => {
      const storeMin = toNumber(storeRules.min_password_length);
      if (storeMin > 0) return storeMin;

      const globalMin = hasGlobalMinLengthKey
        ? toNumber(globalRules.min_password_length)
        : 0;
      if (globalMin > 0) return globalMin;

      if (!hasGlobalMinLengthKey) {
        const sumGlobalMinimums =
          toNumber(globalRules.min_password_numbers) +
          toNumber(globalRules.min_password_lowers) +
          toNumber(globalRules.min_password_uppers) +
          toNumber(globalRules.min_password_specials);

        if (sumGlobalMinimums > 0) return sumGlobalMinimums;

        const globalRandomLength = toNumber(globalRules.random_password_length);
        if (globalRandomLength > 0) return globalRandomLength;
      }

      const datasetMin = toNumber(newPasswordInput.dataset.minLength);
      if (datasetMin > 0) return datasetMin;

      return 8;
    };

    let minLength = resolveBaseMinLength();

    const combinedMinimum = minNumbers + minLowers + minUppers + minSpecials;
    if (combinedMinimum > minLength) {
      minLength = combinedMinimum;
    }

    newPasswordInput.dataset.minLength = minLength;
    newPasswordInput.setAttribute("data-min-length", minLength);
    newPasswordInput.setAttribute("data-validate-minlength", minLength);
    newPasswordInput.dataset.minNumbers = minNumbers;
    newPasswordInput.dataset.minLowers = minLowers;
    newPasswordInput.dataset.minUppers = minUppers;
    newPasswordInput.dataset.minSpecials = minSpecials;
    newPasswordInput.dataset.unsafeChars = unsafeChars;

    const validations =
      newPasswordInput.dataset.validate || "required,min-length";
    if (!validations.includes("password-policy")) {
      newPasswordInput.dataset.validate = `${validations},password-policy`;
    }

    const requirementMessages = [];
    if (minNumbers)
      requirementMessages.push(
        `${minNumbers} number${minNumbers > 1 ? "s" : ""}`
      );
    if (minLowers)
      requirementMessages.push(
        `${minLowers} lowercase letter${minLowers > 1 ? "s" : ""}`
      );
    if (minUppers)
      requirementMessages.push(
        `${minUppers} uppercase letter${minUppers > 1 ? "s" : ""}`
      );
    if (minSpecials)
      requirementMessages.push(
        `${minSpecials} special character${minSpecials > 1 ? "s" : ""}`
      );

    const messageParts = [];
    if (minLength) {
      messageParts.push(`at least ${minLength} characters`);
    }
    if (requirementMessages.length) {
      messageParts.push(`include ${requirementMessages.join(", ")}`);
    }
    if (unsafeChars) {
      messageParts.push(`not contain ${unsafeChars}`);
    }

    const policyMessage =
      messageParts.length > 0
        ? `Password must be ${messageParts.join(" and ")}.`
        : "Password does not meet the configured password policy.";

    newPasswordInput.setAttribute(
      "data-error-message-password-policy",
      policyMessage
    );
  };

  applyPasswordRulesToChangePasswordForm();

  if (userOptionGeneratedPasswordWrapper.el()) {
    // "use this generated password" logic
    const generatedPasswordInput = userOptionGeneratedPasswordWrapper.find(
      '[name="generatedPassword"]',
      true
    );
    const newPasswordInput = changePasswordForm.find(
      '[name="new_password1"]',
      true
    );
    const useThisPasswordButton = userOptionGeneratedPasswordWrapper.find(
      ".use-this",
      true
    );
    const cancelGeneratePasswordButton = _$(
      "#userOptionsGeneratePassword"
    ).find(".cancel-generate-password", true);

    cancelGeneratePasswordButton.addEventListener("click", () => {
      userOptionGeneratedPasswordWrapper.addClass("is-hidden");
    });

    useThisPasswordButton.addEventListener("click", () => {
      newPasswordInput.value = generatedPasswordInput.value;
    });

    if (userOptionsStore.isGeneratePasswordSectionOpen) {
      userOptionGeneratedPasswordWrapper.removeClass("is-hidden");
    }
  }

  // initializing tabs
  const tab = new Tabs({
    el: document.getElementById("userOptionsTab"),
    // updating footer buttons on tab change
    onTabChange: (tabName) => {
      if (twoFactorAuthSetup.QRTimer) clearInterval(twoFactorAuthSetup.QRTimer);

      switch (tabName) {
        case "tab1":
          const preferenceForm = _$("#userOptionsPreferenceForm");

          dialog.update({
            // Footer buttons for preferenceForm
            buttons: [
              {
                label: "save",
                close: true,
                classes: ["primary-button", "mr-3"],
                onClick: () => {
                  const preferenceData = utils.serializeInputs(
                    preferenceForm.el()
                  );
                  localStorage.setItem(
                    "userOptions",
                    JSON.stringify(preferenceData)
                  );

                  store.dispatch("handleSetUserOptions", {
                    preferenceData,
                    userOptions,
                  });
                },
              },
            ],
          });
          break;
        case "tab2":
          const buttons = [
            {
              label: "generateRandomPassword",
              classes: ["secondary-button", "mr-3"],
              onClick: () => {
                _$("#userOptionsGeneratePassword").removeClass("is-hidden");

                const passwordRule = {
                  random_password_length:
                    store?.userInfo.random_password_length,
                  min_password_numbers: store?.userInfo.min_password_numbers,
                  min_password_lowers: store?.userInfo.min_password_lowers,
                  min_password_uppers: store?.userInfo.min_password_uppers,
                  min_password_specials: store?.userInfo.min_password_specials,
                };
                console.log("passwordRule", passwordRule);

                const generatedPassword =
                  utils.generatePasswordBasedOnRules(passwordRule);
                const generatedPasswordInput = _$(
                  "#userOptionsGeneratePassword"
                ).find('[name="generatedPassword"]', true);
                generatedPasswordInput.value = generatedPassword;
              },
            },
            {
              label: "changePassword",
              id: "user-options-change-password",
              classes: ["primary-button", "mr-3"],
              onClick: async (e) => {
                if (changePasswordInFlight) return;
                const validateFields = validate(changePasswordForm.el(), false);
                if (validateFields.length === 0) {
                  const changedPasswordData = utils.serializeInputs(
                    changePasswordForm.el()
                  );
                  const btn =
                    e?.currentTarget ||
                    dialog.root?.querySelector(
                      "#modal-btn-user-options-change-password"
                    );
                  changePasswordInFlight = true;
                  if (btn) {
                    btn.setAttribute("disabled", "disabled");
                    btn.setAttribute("aria-disabled", "true");
                  }
                  try {
                    const result = await changePassword(changedPasswordData);
                    const { response } = result.commandResult;
                    growl("", response, 5000, response !== "success");
                  } catch (error) {
                    console.log(error);
                  } finally {
                    changePasswordInFlight = false;
                    if (btn) {
                      btn.removeAttribute("disabled");
                      btn.removeAttribute("aria-disabled");
                    }
                  }
                }
              },
            },
          ];

          if (customizations.canHideUserOptionGeneratePassword) {
            buttons.shift();
          }
          dialog.update({
            // Footer buttons for changePasswordForm
            buttons,
          });
          break;
        case "tab3":
          const phoneNumberForm = _$("#userOptionsPhoneNumberForm").el();

          savePhoneHandler = async (e) => {
            if (changePhoneInFlight) return;
            if (!phoneNumberForm) return;
            const validateFields = validate(phoneNumberForm, false);

            if (validateFields.length === 0) {
              const phoneNumberFormData =
                utils.serializeInputs(phoneNumberForm);
              const passwordPrompt = await Prompt({
                title: t("currentPassword"),
                inputAttrs: {
                  type: "password",
                  autocomplete: "off",
                },
                confirm: t("ok"),
                cancel: t("cancel"),
                submitOnEnter: true,
              });
              if (!passwordPrompt) return;
              const current_password = (passwordPrompt.value || "").trim();
              if (!current_password) return;

              const btn =
                e?.currentTarget ||
                dialog.root?.querySelector(
                  "#modal-btn-user-options-change-phone"
                );
              changePhoneInFlight = true;
              if (btn) {
                btn.setAttribute("disabled", "disabled");
                btn.setAttribute("aria-disabled", "true");
              }

              try {
                const result = await changePhone({
                  ...phoneNumberFormData,
                  current_password,
                });
                growl("", result, 5000, false);
              } catch (error) {
                if (!error?.canceled) {
                  console.log(error);
                }
              } finally {
                changePhoneInFlight = false;
                if (btn) {
                  btn.removeAttribute("disabled");
                  btn.removeAttribute("aria-disabled");
                }
              }
            }
          };

          dialog.update({
            buttons: [
              {
                label: "save",
                id: "user-options-change-phone",
                classes: ["primary-button", "mr-3"],
                onClick: (e) => savePhoneHandler(e),
              },
            ],
          });
          break;
        case "tab4":
          if (twoFactorAuthSetup.isQRCodeOpen) {
            showQR();
            return;
          }

          _$("#twoFactorAuthQRContainer").addClass("is-hidden");
          _$("#setup2FactorAuthButton").removeClass("is-hidden");

          dialog.update({
            // Footer buttons for 2fa
            buttons: [],
          });
          break;
        default:
          break;
      }
    },
  });

  if (userOptionsStore.activeTabIndex) {
    tab.setIndex(userOptionsStore.activeTabIndex);
  }
};
